import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Edit } from "react-feather";
import Select from "react-select";
import Table from "../../core/pagination/datatable";
import PageHeader from "../components/PageHeader";
import AppointmentModal from "../../core/modals/appointments/AppointmentModal";
import { fetchAppointments } from "../../core/redux/slices/appointmentSlice";
import { fetchDoctors } from "../../core/redux/slices/doctorSlice";
import { fetchProcedures } from "../../core/redux/slices/procedureSlice";
import { formatTime12 } from "../../core/utils/formatTime";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Booked", label: "Booked" },
  { value: "Completed", label: "Completed" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "NoShow", label: "No Show" },
];

const STATUS_BADGE = {
  Booked: "primary",
  Completed: "success",
  Cancelled: "danger",
  NoShow: "warning",
};

const today = () => new Date().toISOString().split("T")[0];

const AppointmentList = () => {
  const dispatch = useDispatch();
  const { list, loading } = useSelector((s) => s.appointments);
  const doctors = useSelector((s) => s.doctors.list);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedDate, setSelectedDate] = useState(today());
  const [doctorFilter, setDoctorFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);

  // Runs once on mount to load the initial (today's) date — subsequent date changes are
  // only applied when the user clicks Search (handleSearch), not automatically, so
  // selectedDate is deliberately excluded from the dependency array.
  useEffect(() => {
    dispatch(fetchDoctors());
    dispatch(fetchProcedures());
    dispatch(fetchAppointments({ date: selectedDate }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleSearch = () => {
    dispatch(fetchAppointments({ date: selectedDate }));
  };

  const doctorOptions = [
    { value: "", label: "All Doctors" },
    ...doctors.map((d) => ({ value: d.doctorID || d.id, label: d.doctorName })),
  ];

  const data = useMemo(() => {
    const q = search.toLowerCase();
    return list.filter((a) => {
      const matchSearch = !q || a.patientName?.toLowerCase().includes(q) || String(a.tokenNumber).includes(q);
      const matchStatus = !statusFilter || a.status === statusFilter;
      const matchDoctor = !doctorFilter || a.doctorId === doctorFilter || a.doctorID === doctorFilter;
      return matchSearch && matchStatus && matchDoctor;
    });
  }, [list, search, statusFilter, doctorFilter]);

  const openAdd = () => { setEditRecord(null); setShowModal(true); };
  const openEdit = (rec) => { setEditRecord(rec); setShowModal(true); };
  const closeModal = () => {
    setShowModal(false);
    setEditRecord(null);
    dispatch(fetchAppointments({ date: selectedDate }));
  };

  const columns = [
    { title: "Token", dataIndex: "tokenNumber", width: 70, align: "center" },
    { title: "Patient", dataIndex: "patientName", sorter: (a, b) => (a.patientName || "").localeCompare(b.patientName || "") },
    { title: "Doctor", dataIndex: "doctorName" },
    { title: "Procedure", dataIndex: "procedureName" },
    { title: "Date", dataIndex: "appointmentDate", width: 110, render: (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—" },
    { title: "From", dataIndex: "fromTime", width: 100, render: formatTime12 },
    { title: "To", dataIndex: "toTime", width: 100, render: formatTime12 },
    {
      title: "Status", dataIndex: "status", width: 110,
      render: (v) => (
        <span className={`badge bg-${STATUS_BADGE[v] || "secondary"}`}>
          {v === "NoShow" ? "No Show" : v}
        </span>
      ),
    },
    {
      title: "Action", dataIndex: "action", width: 80,
      render: (_, rec) => (
        <div className="edit-delete-action d-flex">
          <button className="p-2 btn btn-sm" title="View / Edit" onClick={() => openEdit(rec)}>
            <Edit size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader title="Appointments" subtitle="Manage patient appointments" onAdd={openAdd} addLabel="Book Appointment" />
        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top d-flex gap-2 flex-wrap align-items-center mb-2">
              <div className="d-flex align-items-center gap-1">
                <label className="mb-0 small">Date</label>
                <input type="date" className="form-control form-control-sm" value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)} style={{ width: 150 }} />
              </div>
              <button className="btn btn-sm btn-primary" onClick={handleSearch}>Search</button>
              <div style={{ minWidth: 160 }}>
                <Select classNamePrefix="react-select" options={STATUS_OPTIONS}
                  defaultValue={STATUS_OPTIONS[0]}
                  onChange={(opt) => setStatusFilter(opt?.value || "")} />
              </div>
              <div style={{ minWidth: 180 }}>
                <Select classNamePrefix="react-select" options={doctorOptions}
                  defaultValue={doctorOptions[0]}
                  onChange={(opt) => setDoctorFilter(opt?.value || "")} />
              </div>
              <div className="search-input flex-grow-1">
                <input type="text" placeholder="Search patient or token..."
                  className="form-control form-control-sm formsearch"
                  value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>
            <div className="table-responsive">
              <Table columns={columns} dataSource={data} loading={loading} />
            </div>
          </div>
        </div>
      </div>
      <AppointmentModal show={showModal} onClose={closeModal} editRecord={editRecord} />
    </div>
  );
};

export default AppointmentList;
